import { useCallback } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
	buildLabelsExcludedFromDashboard,
	processPlatformConfiguration
} from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { REFETCH_INTERVAL } from 'src/common/consts/QueryConfigs';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { DASHBOARD_WEB_SDK_TESTS } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { IUseTableQueryParams } from 'src/common/types/UseTableQueryParamsType';

import { getWebSdkTests } from '../services/dashboard/WebSdkTestsService';
import { TWebSdkTestsResult } from '../types/WebSdkDashboardServiceTypes';

interface IUseWebSdkTestsQueryParams extends IUseTableQueryParams {
	showRecentTest: boolean;
	showTestPath: boolean;
	dateRange?: string;
}

export const useWebSdkTestsQuery = ({
	showRecentTest,
	dateRange,
	filters,
	sort,
	page,
	pageSize,
	showTestPath
}: IUseWebSdkTestsQueryParams): [UseQueryResult<TWebSdkTestsResult>, () => void] => {
	const { configuration } = useConfiguration();
	const { coreLabels } = processPlatformConfiguration(configuration?.PLATFORM);
	const labelsExcludedFromDashboard = buildLabelsExcludedFromDashboard(coreLabels);

	const queryResult = useQuery(
		[
			DASHBOARD_WEB_SDK_TESTS,
			showRecentTest,
			filters,
			sort,
			page,
			pageSize,
			dateRange,
			labelsExcludedFromDashboard
		],
		async (): Promise<TWebSdkTestsResult> =>
			getWebSdkTests({
				isRecentTests: showRecentTest,
				dateRange,
				filterOptions: filters,
				sort,
				page,
				pageSize,
				isShowTestPath: showTestPath,
				labelsExcludedFromDashboard
			}),
		{
			keepPreviousData: true,
			refetchInterval: REFETCH_INTERVAL,
			staleTime: REFETCH_INTERVAL
		}
	);

	const invalidate = useCallback(
		() =>
			queryClient.invalidateQueries({ queryKey: [DASHBOARD_WEB_SDK_TESTS], refetchType: 'none' }),
		[]
	);

	return [queryResult, invalidate];
};
